import { Component, ViewChild } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { DateService } from '../../../services/date/date.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { ConfirmationModalComponent } from '../../daily-log/confirmation-modal/confirmation-modal.component';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import {
  WeatherLogAdditionalReadingService
} from '../../../services/weather-readings/weather-log-additional-reading.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import {
  WeatherLogAddStandaloneReadingComponent
} from './components/weather-log-add-standalone-reading/weather-log-add-standalone-reading';
import { WeatherLogStandaloneEditor, WeatherSetup } from '../../../models/weather-readings/weather-readings.models';
import { WeatherReadingsService } from '../../../services/weather-readings/weather-readings.service';
import { VesselService } from '../../../services/vessel/vessel.service';
import { VesselOption } from '../../../models/setup/setup.models';
import {
  WeatherLogStandaloneReadingGridComponent
} from './components/weather-log-standalone-reading-grid/weather-log-standalone-reading-grid';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-weather-log-manage-readings',
  templateUrl: 'weather-log-manage-readings.html',
})
export class WeatherLogManageReadingsPage {
  @ViewChild('standaloneReadingGridComponent') standaloneReadingGridComponent: WeatherLogStandaloneReadingGridComponent;

  config = AppConfig.settings;
  loading: boolean;

  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  timeOfChange: string;
  isWeb: boolean;
  projectId: number;
  selectedProjectName: string;
  weatherSetups: WeatherSetup[];
  vesselEditor: VesselOption;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private toastCtrl: ToastController,
    private lockedDayService: LockedDayService,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private weatherLogAdditionalReadingService: WeatherLogAdditionalReadingService,
    private platform: Platform,
    private modalController: ModalController,
    private navHelperService: NavHelperService,
    private dailyLogService: DailyLogService,
    private weatherReadingService: WeatherReadingsService,
    private vesselService: VesselService,
    private supportContextService: SupportContextService
  ) {
  }

  async ngOnInit() {
    this.supportContextService.setComponentContext(SupportContextKey.WeatherLog, 'Manage Weather Readings', 'WeatherLogManageReadingsPage');

    this.loading = true;
    this.isWeb = this.platform.is('core');

    // update daily log date with date from dailyLogService
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    await this.loadWeatherSetups();
    await this.loadVesselEditor();

    // create adjusted date with day part of the daily log date, but the time part from the local machine time
    const newDate = DateTime.local();
    const adjustedDate = this.dailyLogDate.dateTime.set({
      year: this.dailyLogDate.dateTime.year,
      month: this.dailyLogDate.dateTime.month,
      day: this.dailyLogDate.dateTime.day,
      hour: newDate.hour,
      minute: newDate.minute,
      second: 0,
      millisecond: 0
    });

    this.timeOfChange = this.dateService.setTimeStringFromDateTime(adjustedDate);
    this.loading = false;
  }

  async loadWeatherSetups() {
    this.weatherSetups = await this.weatherReadingService.getWeatherSetupsForDay(this.dailyLogDate);
    this.projectId = this.weatherSetups[0].projectId;
    this.selectedProjectName = this.weatherSetups[0].projectName;
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Leg Load Frequency Updated',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  openAddReadingModal() {
    const modal = this.modalController.create(WeatherLogAddStandaloneReadingComponent, {
      isWeb: this.isWeb,
      dailyLogDate: this.dailyLogDate,
      projectId: this.projectId,
      vesselId: this.vesselEditor.id
    }, {
      cssClass: 'weather-log-add-reading'
    });

    modal.present();

    modal.onDidDismiss(async data => {
      if (data && data.choice) {
        // flag the leg load log to reload once the user goes back so the new frequency takes effect
        this.navHelperService.updateWeatherLogRequiresReload(true);

        await this.ngOnInit();
      }
    })
  }

  openDeleteReadingConfirmationModal(row: WeatherLogStandaloneEditor) {
    const confirmationModal = this.modalController.create(ConfirmationModalComponent, {
      title: 'Delete Confirmation',
      comment: `Record: ${row.readingTimeShow}`,
      message: 'Are you sure you want to remove this additional reading?',
      optionalMessage: 'Any data already entered in this row will be lost.',
      trueText: 'Confirm',
      falseText: 'Cancel',
      showButtons: true
    }, {cssClass: 'daily-log-modal-four-line'});

    confirmationModal.present();

    confirmationModal.onDidDismiss(async data => {
      if (data && data.choice) {
        await this.deleteStandaloneReading(row);
      }
    })
  }

  private async deleteStandaloneReading(editor: WeatherLogStandaloneEditor) {
    // flag the leg load log to reload once the user goes back so the new frequency takes effect
    this.navHelperService.updateWeatherLogRequiresReload(true);

    await this.weatherLogAdditionalReadingService.deleteWeatherLogStandaloneEditor(editor).then(() => {
      this.ngOnInit();
    });
  }

  private async loadVesselEditor() {
    this.vesselEditor = await this.vesselService.getEditor();

  }

  projectSelected($selectedProjectId: any) {
    this.selectedProjectName = this.weatherSetups.find(e => e.projectId == $selectedProjectId).projectName;
    this.standaloneReadingGridComponent.loadGrid($selectedProjectId);
  }
}



// WEBPACK FOOTER //
// ./src/pages/weather-readings/weather-log-manage-readings/weather-log-manage-readings.ts